import React, { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { authApi } from '@app/store/services/auth';
import styled from '@2pulse/responsive';
import { KeyboardAvoidingView, TouchableOpacity } from 'react-native';
import { TextInput } from '@app/components';
import { Button, Form } from '@app/components';
import { StyledForms } from '@app/components/styled/form/Form';
import { useToast } from '@app/hooks';
import { extractApiGenericError } from '@app/helpers';
import { useNavigation } from '@react-navigation/native';

const ForgotPasswordView = styled.View({
  width: '100%',
  height: '100%',
  flex: 1,
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: '#02182B',
});

const ForgotPasswordForm = styled.View({
  width: 420,
});

const ForgotPasswordSubmit = styled.View({
  width: 300,
  marginLeft: 60,
});

const Logo = styled.Image({
  marginTop: 21,
  marginBottom: 34,
  alignSelf: 'center',
  height: 70,
  width: 345,
});

const TextError = styled.Text({
  color: 'red',
  marginBottom: 10,
});

const Title = styled.Text({
  color: 'white',
  fontSize: 24,
  fontWeight: 'bold',
  textAlign: 'center',
  marginBottom: 10,
});

const Subtitle = styled.Text({
  color: 'white',
  fontSize: 14,
  textAlign: 'center',
  marginBottom: 20,
});

const BackLink = styled.Text({
  color: 'white',
  fontSize: 14,
  textAlign: 'center',
  marginTop: 20,
  textDecorationLine: 'underline',
});

const schema = yup
  .object({
    email: yup.string().email('Veuillez entrer un email valide').required('Ce champ est requis'),
  })
  .required();

export const ForgotPassword = () => {
  const toast = useToast();
  const navigation = useNavigation();
  const [success, setSuccess] = useState(false);

  const formMethods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
    },
  });

  const { errors } = formMethods.formState;

  const [forgetPassword, { isLoading }] = authApi.useForgetPasswordMutation();

  const onSubmit = async (data: { email: string }) => {
    try {
      await forgetPassword({ email: data.email }).unwrap();
      setSuccess(true);
    } catch (error: any) {
      const errorMessage = extractApiGenericError(error) ?? 'Une erreur est survenue.';
      toast.error(errorMessage);
    }
  };

  const handleGoBack = () => {
    navigation.navigate('Login' as never);
  };

  if (success) {
    return (
      <ForgotPasswordView>
        <KeyboardAvoidingView behavior="position">
          <Logo source={require('@app/assets/img/logo.png')} />

          <Title>Email envoy&eacute;</Title>
          <Subtitle>
            Si un compte existe avec cet email, vous recevrez un lien pour r&eacute;initialiser
            votre mot de passe.
          </Subtitle>

          <ForgotPasswordSubmit style={{ marginLeft: 60 }}>
            <Button large title="Retour &agrave; la connexion" onPress={handleGoBack} />
          </ForgotPasswordSubmit>
        </KeyboardAvoidingView>
      </ForgotPasswordView>
    );
  }

  return (
    <ForgotPasswordView>
      <KeyboardAvoidingView behavior="position">
        <Logo source={require('@app/assets/img/logo.png')} />

        <Title>Mot de passe oubli&eacute;</Title>
        <Subtitle>
          Entrez votre adresse email pour recevoir un lien de r&eacute;initialisation.
        </Subtitle>

        {errors.email && <TextError>{errors.email.message}</TextError>}

        <ForgotPasswordForm>
          <Form formMethods={formMethods}>
            <StyledForms.Form>
              <TextInput
                name="email"
                label="Email"
                placeholderTextColor="black"
                autoCapitalize="none"
                autoComplete="off"
                onSubmitEditing={formMethods.handleSubmit(onSubmit)}
              />

              <ForgotPasswordSubmit>
                <Button
                  large
                  title="Envoyer le lien"
                  loading={isLoading}
                  onPress={formMethods.handleSubmit(onSubmit)}
                />
              </ForgotPasswordSubmit>
            </StyledForms.Form>
          </Form>
        </ForgotPasswordForm>

        <TouchableOpacity onPress={handleGoBack}>
          <BackLink>Retour &agrave; la connexion</BackLink>
        </TouchableOpacity>
      </KeyboardAvoidingView>
    </ForgotPasswordView>
  );
};
